import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { 
  CreditCard, Plus, Edit, Trash2, Star,
  CheckCircle, AlertTriangle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE_URL } from '@/lib/authFetch';

interface Plano {
  id: number;
  nome: string;
  slug: string;
  descricao: string;
  valor: number;
  recursos: string[];
  ativo: boolean;
  destaque: boolean;
  created_at?: string;
}

const PlanoManagement = () => {
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlano, setEditingPlano] = useState<Plano | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [planoToDelete, setPlanoToDelete] = useState<Plano | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state simplificado
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    valor: '',
    recursos: '',
    ativo: true,
    destaque: false
  });

  useEffect(() => {
    loadPlanos();
  }, []);

  const loadPlanos = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/planos`);
      const data = await response.json();
      setPlanos(data.planos || []);
    } catch (error) {
      toast.error('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (plano?: Plano) => {
    if (plano) {
      setEditingPlano(plano);
      setFormData({
        nome: plano.nome,
        descricao: plano.descricao || '',
        valor: String(plano.valor),
        recursos: plano.recursos?.join('\n') || '',
        ativo: plano.ativo,
        destaque: plano.destaque
      });
    } else {
      setEditingPlano(null);
      setFormData({
        nome: '',
        descricao: '',
        valor: '',
        recursos: '',
        ativo: true,
        destaque: false
      });
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.nome || !formData.valor) {
      toast.error('Preencha nome e valor');
      return;
    }

    setSaving(true);

    try {
      // Gera slug automaticamente a partir do nome
      const slug = formData.nome.toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remove acentos
        .replace(/[^a-z0-9]+/g, '-') // Substitui caracteres especiais por -
        .replace(/^-+|-+$/g, ''); // Remove - do início e fim

      const payload = {
        nome: formData.nome,
        slug: editingPlano?.slug || slug,
        descricao: formData.descricao,
        valor: parseFloat(formData.valor),
        max_cameras: -1,
        max_usuarios: -1,
        max_consultas_mes: -1,
        dias_armazenamento: 30,
        recursos: formData.recursos.split('\n').filter(r => r.trim()),
        ativo: formData.ativo,
        destaque: formData.destaque,
        ordem: planos.length,
        dias_trial: 0
      };

      const url = editingPlano 
        ? `${API_BASE_URL}/admin/planos/${editingPlano.id}`
        : `${API_BASE_URL}/admin/planos`;
      
      const method = editingPlano ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingPlano ? 'Plano atualizado!' : 'Plano criado!');
        setModalOpen(false);
        loadPlanos();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao salvar plano');
      }
    } catch (error) {
      toast.error('Erro ao salvar plano');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!planoToDelete) return;

    try {
      const response = await fetch(`${API_BASE_URL}/admin/planos/${planoToDelete.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Plano deletado!');
        setDeleteModalOpen(false);
        loadPlanos();
      }
    } catch (error) {
      toast.error('Erro ao deletar plano');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Stats
  const totalPlanos = planos.length;
  const planosAtivos = planos.filter(p => p.ativo).length;
  const planosInativos = planos.filter(p => !p.ativo).length;

  return (
    <div className="space-y-6">
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-protec-gold" />
                Gerenciamento de Planos
              </CardTitle>
              <CardDescription className="text-gray-300">
                Crie e gerencie os planos de assinatura do sistema
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                size="sm"
                className="bg-protec-gold text-black"
                onClick={() => handleOpenModal()}
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Plano
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-protec-dark rounded-lg p-4 text-center">
              <CreditCard className="w-5 h-5 text-protec-gold mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{totalPlanos}</p>
              <p className="text-xs text-gray-400">Total</p>
            </div>
            <div className="bg-protec-dark rounded-lg p-4 text-center">
              <CheckCircle className="w-5 h-5 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{planosAtivos}</p>
              <p className="text-xs text-gray-400">Ativos</p>
            </div>
            <div className="bg-protec-dark rounded-lg p-4 text-center">
              <AlertTriangle className="w-5 h-5 text-yellow-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{planosInativos}</p>
              <p className="text-xs text-gray-400">Inativos</p>
            </div>
          </div>

          {/* Lista de Planos */}
          {loading ? (
            <div className="text-center py-8 text-gray-400">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
              Carregando planos...
            </div>
          ) : planos.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <CreditCard className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Nenhum plano cadastrado</p>
              <p className="text-sm mt-2">Crie seu primeiro plano clicando no botão acima</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {planos.map((plano) => (
                <Card 
                  key={plano.id} 
                  className={`bg-protec-dark border transition-all ${
                    plano.destaque 
                      ? 'border-protec-gold shadow-lg shadow-protec-gold/10' 
                      : plano.ativo 
                        ? 'border-gray-700' 
                        : 'border-red-500/30 opacity-60'
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-white">{plano.nome}</h3>
                          {plano.destaque && (
                            <Star className="w-4 h-4 text-protec-gold fill-protec-gold" />
                          )}
                        </div>
                        {plano.descricao && (
                          <p className="text-xs text-gray-500 mt-1">{plano.descricao}</p>
                        )}
                      </div>
                      <Badge className={plano.ativo ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                        {plano.ativo ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>
                    
                    <p className="text-2xl font-bold text-protec-gold mb-3">
                      {formatCurrency(plano.valor)}<span className="text-sm text-gray-400">/mês</span>
                    </p>

                    {/* Recursos */}
                    {plano.recursos && plano.recursos.length > 0 && (
                      <div className="mb-4 space-y-1">
                        {plano.recursos.slice(0, 3).map((recurso, idx) => (
                          <p key={idx} className="text-xs text-gray-400 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500" />
                            {recurso}
                          </p>
                        ))}
                        {plano.recursos.length > 3 && (
                          <p className="text-xs text-gray-500">+{plano.recursos.length - 3} mais...</p>
                        )}
                      </div>
                    )}

                    <div className="flex gap-2 mt-4">
                      <Button 
                        size="sm" 
                        className="flex-1 bg-amber-600/20 text-amber-400 hover:bg-amber-600/40 border-0"
                        onClick={() => handleOpenModal(plano)}
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        size="sm" 
                        className="bg-rose-600/20 text-rose-400 hover:bg-rose-600/40 border-0"
                        onClick={() => {
                          setPlanoToDelete(plano);
                          setDeleteModalOpen(true);
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Criar/Editar - SIMPLIFICADO */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingPlano ? 'Editar Plano' : 'Novo Plano'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-gray-300">Nome do Plano *</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: Plano Básico"
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-gray-300">Descrição</Label>
              <Input
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Ideal para pequenos negócios..."
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-gray-300">Valor Mensal (R$) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.valor}
                onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                placeholder="99.90"
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-gray-300">Recursos (um por linha)</Label>
              <Textarea
                value={formData.recursos}
                onChange={(e) => setFormData({ ...formData, recursos: e.target.value })}
                placeholder={"Acesso completo\nSuporte prioritário\nRelatórios avançados"}
                rows={4}
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-protec-dark rounded-lg">
              <div>
                <Label className="text-gray-300">Plano Ativo</Label>
                <p className="text-xs text-gray-500">Visível para clientes</p>
              </div>
              <Switch
                checked={formData.ativo}
                onCheckedChange={(checked) => setFormData({ ...formData, ativo: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-protec-dark rounded-lg">
              <div>
                <Label className="text-gray-300">Destaque</Label>
                <p className="text-xs text-gray-500">Mostrar como "Mais Popular"</p>
              </div>
              <Switch
                checked={formData.destaque}
                onCheckedChange={(checked) => setFormData({ ...formData, destaque: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-protec-gold text-black w-full"
            >
              {saving ? 'Salvando...' : (editingPlano ? 'Atualizar' : 'Criar Plano')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-gray-300 py-4">
            Tem certeza que deseja excluir o plano <strong>{planoToDelete?.nome}</strong>?
            <br />
            <span className="text-red-400 text-sm">Esta ação não pode ser desfeita.</span>
          </p>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteModalOpen(false)}
              className="border-gray-500 text-gray-400"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlanoManagement;
